import { dateTimeFormat } from 'localization'
import { parseDate } from 'models/utils'
import { getWorkingDaysBetweenDates } from 'utils/getWorkingDaysBetweenDates'

export const mapSurgeryClinicalApprovalRequestDetails = (
	requisition,
	nextAppointment,
	i18n
) => {
	const currentDate = parseDate(new Date().toISOString())
	const workingDays = getWorkingDaysBetweenDates(
		currentDate?.date?.momentDate,
		nextAppointment?.date?.momentDate
	)

	return [
		{
			title: i18n`Next appointment`,
			text: nextAppointment?.date?.shortDateFormat
				? workingDays !== -1
					? i18n`${
							nextAppointment.date.shortDateFormat
					  } (in ${workingDays} ${
							workingDays === 1
								? i18n`working day`
								: i18n`working days`
					  })`
					: i18n`Without scheduled appointment`
				: '-',
			icon: 'next',
		},
		{
			title: i18n`Requiring entity`,
			text: requisition?.requiringEstablishment?.name || '-',
			icon: 'location-alt',
		},
		{
			title: i18n`Transport`,
			text: requisition?.transport || '-',
			icon: 'car',
		},
		{
			title: i18n`Prescribing doctor`,
			text: requisition?.prescribingDoctor || '-',
			icon: 'medical-info',
		},
		{
			title: i18n`Clinical Information`,
			text: requisition?.clinicalInformation || '-',
			icon: 'clinical-notes',
		},
		{
			title: i18n`Observations`,
			text: requisition?.requisitionObservation || '-',
			icon: 'requisition',
		},
	]
}

export const mapSurgeryClinicalApprovalRequisitionDetails = (
	requisition,
	i18n
) => {
	const details = [
		{
			title: i18n`Surgery Date`,
			text:
				requisition?.surgicalRequestAdmission?.surgeryDate
					?.shortDateFormat || '-',
		},
		{
			title: i18n`Destination provider`,
			text: requisition?.establishment?.name || '-',
		},
		{
			title: i18n`Surgery Type`,
			text:
				requisition?.surgicalRequestAdmission?.surgeryType?.name || '-',
		},
		{
			title: i18n`Requires Block`,
			text: i18n([
				requisition?.surgicalRequestAdmission?.needsOperatingRoom ||
					'-',
			]),
		},
	]

	if (requisition?.surgicalRequestAdmission?.surgeryType?.id === 6) {
		const newDate =
			requisition?.surgicalRequestAdmission?.admissionDate?.momentDate
				.clone()
				.add(
					requisition?.surgicalRequestAdmission?.admissionDays || 0,
					'd'
				)
				.format(dateTimeFormat.date || 'MMM DD, YYYY')

		details.push({
			title: i18n`Internment`,
			text: i18n`from ${requisition?.surgicalRequestAdmission?.admissionDate?.shortDateFormat} to ${newDate} (${requisition?.surgicalRequestAdmission?.admissionDays} days)`,
		})
	}

	details.push({
		title: i18n`Surgery materials`,
		text: requisition?.materials || '-',
	})

	return details
}

export const mapNonSurgicalAdmissionDetails = (requisition, i18n) => {
	const newDate =
		requisition?.surgicalRequestAdmission?.admissionDate?.momentDate
			.clone()
			.add(requisition?.surgicalRequestAdmission?.admissionDays || 0, 'd')
			.format(dateTimeFormat.date || 'MMM DD, YYYY')

	const details = [
		{
			title: i18n`Destination provider`,
			text: requisition?.establishment?.name || '-',
		},
		{
			title: i18n`Internment`,
			text: i18n`from ${requisition?.surgicalRequestAdmission?.admissionDate?.shortDateFormat} to ${newDate} (${requisition?.surgicalRequestAdmission?.admissionDays} days)`,
		},
	]

	return details
}

export const mapOpinionTaskDetails = (medicalOpinion, i18n) => {
	return [
		{
			title: i18n`Requested By`,
			text: medicalOpinion?.creationUser?.name || '-',
			icon: 'profile',
		},
		{
			title: i18n`Reason`,
			text: medicalOpinion?.submittedMedicalRequest || '-',
			icon: 'info',
		},
	]
}

export const mapTransferOpinionDetails = (requisition, process, i18n) => {
	return [
		{
			title: i18n`Requiring entity`,
			text: requisition?.requiringEstablishment?.name || '-',
			icon: 'location-alt',
		},
		{
			title: i18n`Injured district`,
			text: process?.injured?.district || '-',
			icon: 'location-user',
		},
		{
			title: i18n`Requiring specialty`,
			text: requisition?.specialty?.name || '-',
			icon: 'leading',
		},
		{
			title: i18n`Reason`,
			text: requisition?.requisitionObservation || '-',
			icon: 'info',
		},
	]
}

export const mapRelapseClinicalApprovalDetails = (requisition, i18n) => {
	return [
		{
			title: i18n`Applicant entity`,
			text: requisition?.applicant?.name || '-',
			icon: 'location-alt',
		},
		{
			title: i18n`Asked by`,
			text: requisition?.creationUser || '-',
			icon: 'profile',
		},
		{
			title: i18n`Reason`,
			text: requisition?.processRequestMotive || '-',
			icon: 'info',
		},
	]
}

export const mapClinicalAppointmentApprovalDetails = (requisition, i18n) => {
	return [
		{
			title: i18n`Service provider`,
			text: requisition?.tstAppointment.serviceProvider || '-',
			icon: 'location-alt',
		},
		{
			title: i18n`Appointment Date`,
			text:
				requisition?.tstAppointment?.appointmentDate?.dateTimeFormat ||
				i18n`To schedule`,
			icon: 'calendar',
		},
		{
			title: i18n`Specialty`,
			text: requisition?.tstAppointment?.specialty || '-',
			icon: 'leading',
		},
		{
			title: i18n`Transport`,
			text: requisition?.tstAppointment?.transport || '-',
			icon: 'transport',
		},
		{
			title: i18n`Appointment doctor`,
			text: requisition?.tstAppointment?.doctor || '-',
			icon: 'medical-info',
		},
		{
			title: i18n`Reason`,
			text: requisition?.tstAppointment?.observation || '-',
			icon: 'info',
		},
	]
}

export const checkIfIsPhysiotherapyOrDentalMedicine = (requisition) => {
	return [4, 8].includes(requisition?.type?.id)
}

export const checkIfIsDentalMedicine = (requisition) => {
	return requisition?.type?.id === 8
}

export const mapMedicalActsDetails = (requisition, nextAppointment, i18n) => {
	const isPhysiotherapyOrDentalMedicine =
		checkIfIsPhysiotherapyOrDentalMedicine(requisition)

	const isDentalMedicine = checkIfIsDentalMedicine(requisition)

	const details = [
		{
			title: i18n`Prescribing doctor`,
			text: requisition?.prescribingDoctor || '-',
			icon: 'medical-info',
		},
		{
			title: i18n`Requiring provider`,
			text: requisition?.requiringEstablishment?.name || '-',
			icon: 'location-alt',
		},
		{
			title: isPhysiotherapyOrDentalMedicine
				? i18n`Start of treatment`
				: i18n`Booking date`,
			text: requisition?.scheduledDate?.shortDateFormat || '-',
			icon: 'calendar',
		},
	]

	if (isPhysiotherapyOrDentalMedicine) {
		details.push({
			title: i18n`End of treatment`,
			text: requisition?.executionDate?.shortDateFormat || '-',
			icon: 'calendar',
		})
	}

	const currentDate = parseDate(new Date().toISOString())
	const workingDays = getWorkingDaysBetweenDates(
		currentDate?.date?.momentDate,
		nextAppointment?.date?.momentDate
	)

	details.push(
		{
			title: i18n`Next Appointment`,
			text: nextAppointment?.date?.shortDateFormat
				? workingDays !== -1
					? i18n`${
							nextAppointment.date.shortDateFormat
					  } (in ${workingDays} ${
							workingDays === 1
								? i18n`working day`
								: i18n`working days`
					  })`
					: i18n`Without scheduled appointment`
				: '-',
			icon: 'next',
		},
		{
			title: i18n`Transport`,
			text: requisition?.transport || '-',
			icon: 'car',
		},
		{
			title: i18n`Clinical Information`,
			text: requisition?.clinicalInformation || '-',
			icon: 'clinical-notes',
		},
		{
			title: i18n`Observations`,
			text: requisition?.requisitionObservation || '-',
			icon: 'requisition',
		}
	)

	if (isDentalMedicine) {
		details.push({
			title: i18n`Costs forecast`,
			text: requisition?.amountsToCharge?.expectedValue
				? `${requisition.amountsToCharge.expectedValue} €`
				: '-',
			icon: 'euro',
		})
	}

	return details
}

const evaluateBoolValueToReturnYesOrNo = (i18n, value) => {
	return value ? i18n`Yes` : i18n`No`
}

export const additionalMedicalActsDetailsForPhysiotherapy = (
	requisition,
	i18n
) => {
	const details = [
		{
			title: i18n`Post-Surgery`,
			text:
				evaluateBoolValueToReturnYesOrNo(
					i18n,
					requisition?.physiotherapyData?.postSurgery
				) || '-',
		},
	]

	if (requisition?.physiotherapyData?.postSurgery) {
		details.push(
			{
				title: i18n`Performed surgery`,
				text: requisition?.physiotherapyData?.performedSurgery || '-',
			},
			{
				title: i18n`Surgery date`,
				text:
					requisition?.physiotherapyData?.surgeryDate
						?.shortDateFormat || '-',
			}
		)
	}

	details.push(
		{
			title: i18n`Osteosynthesis Material`,
			text:
				evaluateBoolValueToReturnYesOrNo(
					i18n,
					requisition?.physiotherapyData?.osteosynthesisMaterial
				) || '-',
		},
		{
			title: i18n`Immobilized body area`,
			text:
				evaluateBoolValueToReturnYesOrNo(
					i18n,
					requisition?.physiotherapyData?.immobilizationBodyArea
				) || '-',
		}
	)

	if (requisition?.physiotherapyData?.immobilizationBodyArea) {
		details.push({
			title: i18n`Immobilization duration`,
			text:
				i18n`${requisition?.physiotherapyData?.immobilizationDuration} dias` ||
				'-',
		})
	}

	details.push({
		title: i18n`Orthoses`,
		text:
			evaluateBoolValueToReturnYesOrNo(
				i18n,
				requisition?.physiotherapyData?.orthoses
			) || '-',
	})

	if (requisition?.physiotherapyData?.orthoses) {
		details.push({
			title: i18n`Orthoses weaning since`,
			text:
				requisition?.physiotherapyData?.orthosesFrom?.shortDateFormat ||
				'-',
		})
	}

	details.push({
		title: i18n`Walking auxiliaries`,
		text:
			evaluateBoolValueToReturnYesOrNo(
				i18n,
				requisition?.physiotherapyData?.walkingAuxiliaries
			) || '-',
	})

	if (requisition?.physiotherapyData?.walkingAuxiliaries) {
		details.push(
			{
				title: i18n`Partial walking auxiliaries weaning since`,
				text:
					requisition?.physiotherapyData?.partialWeaning
						?.shortDateFormat || '-',
			},
			{
				title: i18n`Full walking auxiliaries weaning since`,
				text:
					requisition?.physiotherapyData?.totalWeaning
						?.shortDateFormat || '-',
			}
		)
	}

	return details
}
